import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { stockCountsApi, warehousesApi } from '../../api/warehouse';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';
import { formatDate } from '../../utils/formatDate';

export const STOCK_COUNT_STATUS_LABELS: Record<string, string> = {
  planned: 'Planned', in_progress: 'In Progress', submitted: 'Submitted', reviewed: 'Reviewed', approved: 'Approved', closed: 'Closed',
};
const COUNT_TYPE_LABELS: Record<string, string> = { full: 'Full Stock Count', cycle: 'Cycle Count', spot_check: 'Spot Check' };

interface CreateCountForm { warehouseId: number; countType: string; countDate: string; countTeam?: string; }

export function StockCountsListPage() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { register, handleSubmit, reset } = useForm<CreateCountForm>({ defaultValues: { countType: 'full' } });

  const { data, isLoading, isError } = useQuery({ queryKey: ['stock-counts'], queryFn: () => stockCountsApi.list() });
  const { data: warehousesData } = useQuery({ queryKey: ['warehouses'], queryFn: () => warehousesApi.list() });
  const warehouses = warehousesData?.data ?? [];

  const createMutation = useMutation({
    mutationFn: (input: CreateCountForm) => stockCountsApi.create({ ...input, warehouseId: Number(input.warehouseId) }),
    onSuccess: (result) => {
      showSuccess(`Stock count ${result.data.countReference} created.`);
      queryClient.invalidateQueries({ queryKey: ['stock-counts'] });
      setShowCreateForm(false);
      reset();
      navigate(`/logistics/stock-counts/${result.data.id}`);
    },
    onError: (err: ApiError) => showError(err.message),
  });

  const counts = data?.data ?? [];

  return (
    <div className="page">
      <PageHeader title="Stock Counts" description="Plan and conduct physical stock counts. Approved variances automatically create Stock Adjustments for separate approval." />

      <SectionHeader title="Counts" actions={
        <button onClick={() => setShowCreateForm((s) => !s)}>{showCreateForm ? 'Cancel' : 'New Stock Count'}</button>
      } />

      {showCreateForm && (
        <form className="inline-form" onSubmit={handleSubmit((d) => createMutation.mutate(d))}>
          <label>Warehouse
            <select {...register('warehouseId', { required: true, valueAsNumber: true })}>
              <option value="">Select…</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </label>
          <label>Count Type
            <select {...register('countType')}>
              {Object.entries(COUNT_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label>Count Date <input type="date" {...register('countDate', { required: true })} /></label>
          <label>Count Team <input placeholder="Names of counting staff" {...register('countTeam')} /></label>
          <button type="submit" disabled={createMutation.isPending}>Create</button>
        </form>
      )}

      {isError && <div className="error-state">Failed to load stock counts. Please try refreshing the page.</div>}
      {isLoading ? <div className="loading-state"><span className="loading-spinner" />Loading…</div> : (
        <DataTable
          rows={counts}
          rowKey={(c) => c.id}
          columns={[
            { header: 'Reference', render: (c) => <a onClick={() => navigate(`/logistics/stock-counts/${c.id}`)} className="table-link">{c.count_reference}</a> },
            { header: 'Warehouse', render: (c) => c.warehouse_name },
            { header: 'Type', render: (c) => COUNT_TYPE_LABELS[c.count_type] ?? c.count_type },
            { header: 'Count Date', render: (c) => formatDate(c.count_date) },
            { header: 'Status', render: (c) => <StatusBadge status={c.status} label={STOCK_COUNT_STATUS_LABELS[c.status]} /> },
          ]}
        />
      )}
    </div>
  );
}
